import Ajv, { type ErrorObject, type Schema } from 'ajv';
import addFormats from 'ajv-formats';
import { useCallback, useMemo, useState } from 'react';

import { Button } from './Button';

export interface JsonEditorProps<T = unknown> {
  /** Initial JSON value. */
  value: T;
  /** Optional JSON Schema to validate against. */
  schema?: Schema;
  /** Called with the parsed valid payload when the user clicks Apply. */
  onApply?: (next: T) => void;
  /** Called when the user clicks Reset. Returns the value to render next. */
  onReset?: () => T;
  /** Read-only display; hides edit/apply controls. */
  readOnly?: boolean;
  /** Visible label for the field. */
  label?: string;
  /** Height of the textarea in rem. */
  heightRem?: number;
  className?: string;
}

/**
 * Minimal JSON editor with AJV validation.
 *
 * Design
 * ------
 * - Textarea, not a full code editor (Monaco/CodeMirror) — we want the
 *   prototype bundle small. Users editing real manifests will inherit a
 *   richer editor in Stage 4.
 * - Parse + validate on *every* change so errors surface immediately; the
 *   Apply button stays disabled while the buffer is invalid.
 * - `onApply` only fires for structurally valid JSON that passes the schema
 *   — callers never receive garbage.
 */
export function JsonEditor<T = unknown>({
  value,
  schema,
  onApply,
  onReset,
  readOnly = false,
  label,
  heightRem = 18,
  className = '',
}: JsonEditorProps<T>) {
  const [buffer, setBuffer] = useState(() => JSON.stringify(value, null, 2));
  const [dirty, setDirty] = useState(false);

  const ajv = useMemo(() => {
    const a = new Ajv({ allErrors: true, strict: false });
    addFormats(a);
    return a;
  }, []);

  const validator = useMemo(() => (schema ? ajv.compile(schema) : null), [ajv, schema]);

  const parsedResult = useMemo(() => parseJson(buffer), [buffer]);

  const schemaErrors: ErrorObject[] | null = useMemo(() => {
    if (!parsedResult.ok || !validator) return null;
    return validator(parsedResult.value) ? null : (validator.errors ?? []);
  }, [parsedResult, validator]);

  const onChange = useCallback((next: string) => {
    setBuffer(next);
    setDirty(true);
  }, []);

  const apply = useCallback(() => {
    if (!parsedResult.ok) return;
    if (schemaErrors && schemaErrors.length > 0) return;
    onApply?.(parsedResult.value as T);
    setDirty(false);
  }, [parsedResult, schemaErrors, onApply]);

  const reset = useCallback(() => {
    const next = onReset ? onReset() : value;
    setBuffer(JSON.stringify(next, null, 2));
    setDirty(false);
  }, [onReset, value]);

  const parseError = parsedResult.ok ? null : parsedResult.error;
  const hasErrors = Boolean(parseError) || (schemaErrors?.length ?? 0) > 0;

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <label className="block text-sm font-medium text-ink">{label}</label>}
      <textarea
        aria-label={label ?? 'JSON editor'}
        className="w-full font-mono text-xs rounded-md border border-border bg-paper p-3 focus:outline-none focus:ring-2 focus:ring-brand"
        style={{ height: `${heightRem}rem` }}
        value={buffer}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        spellCheck={false}
      />

      {hasErrors && (
        <div role="alert" className="rounded-md border border-danger/30 bg-danger/5 p-3 text-xs text-danger space-y-1">
          {parseError && <div>Parse error: {parseError}</div>}
          {schemaErrors?.map((e, i) => (
            <div key={i}>
              <code className="font-mono">{e.instancePath || '/'}</code> {e.message}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="flex gap-2">
          <Button variant="primary" onClick={apply} disabled={!dirty || hasErrors}>
            Apply
          </Button>
          <Button variant="secondary" onClick={reset} disabled={!dirty}>
            Reset
          </Button>
        </div>
      )}
    </div>
  );
}

type ParseResult = { ok: true; value: unknown } | { ok: false; error: string };

function parseJson(text: string): ParseResult {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'invalid JSON' };
  }
}
